import React, { useContext, useMemo } from 'react';
import { CancelButton } from '../../../../../../VentoryUI/components/common/Button';
import { TextInput } from '../../../../../components/TextInput';
import { t, translate } from '../../../../../../types/translation/Translator';
import { TranslationKey } from '../../../../../../types/translation/TranslationKey';
import { ProductContext } from '../../../../../../context/ProductContext';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { StockLocationContext } from '../../../../../../context/StockLocationContext';
import { ProductMasterDataContext } from '../../../../../../context/ProductMasterDataContext';
import { BinContext } from '../../../../../../context/BinContext';
import { useToast } from '../../../../../../providers/ToastProvider';
import { ActionModalPane } from '../../../../../components/Modal';
import { LotContext } from '../../../../../../context/LotContext';
import Table, { TableHeader } from '../../../../../components/Table';
import { Product } from '../../../../../../types/product';
import { KitTemplateContext } from '../../../../../../context/KitTemplateContext';
import { KitContext } from '../../../../../../context/KitContext';
import { ContainerContext } from '../../../../../../context/ContainerContext';
import { UnitOfMeasure } from '../../../../../../types/uom';
import { UnitOfMeasureUtil } from '../../../../../../types/uom';
import { ProductTableSettingsColumn } from '../../../../../../types/table_settings/product/ProductTableSettings';
import { CreateOrderFromStockModalContext } from '../../../../../../providers/CreateOrderFromStockModalProvider';
import { Button, ButtonVariant } from '../../../../../components/Button';

interface QuickActionDetailViewProps {
  setOpen: (open: boolean) => void;
  pmdId: string;
  productId?: string;
}

export default function QuickActionDetailView({ setOpen, pmdId, productId }: QuickActionDetailViewProps) {
  const { showToast } = useToast();

  const { currentCompany } = useContext(CompanyContext);
  const { stockLocations } = useContext(StockLocationContext);
  const { products } = useContext(ProductContext);
  const { productMasterData } = useContext(ProductMasterDataContext);
  const { bins } = useContext(BinContext);
  const { lots } = useContext(LotContext);
  const { kits } = useContext(KitContext);
  const { kitTemplates } = useContext(KitTemplateContext);
  const { containers } = useContext(ContainerContext);
  const { openCreateOrderFromStockModal } = useContext(CreateOrderFromStockModalContext);

  const pmd = productMasterData.get(pmdId);

  const product = useMemo(() => {
    return [...(products.get(pmdId)?.values() || [])].find(
      p => p.companyId === currentCompany.id && p.id === productId,
    );
  }, [productId]);

  if (!product || !pmd) return null;

  const kitProducts = useMemo(() => {
    if (!pmd.kitManaged || !product.kitId) return [];

    const kit = kits.get(product.kitId || '');
    const kitTemplate = kitTemplates.get(kit?.templateId || '');
    if (!kitTemplate || !kit) return [];

    return kitTemplate.products.map(kp => {
      const item = [...products.values()]
        .flatMap(i => [...i.values()])
        .flat()
        .find(p => p.kitId === kit.id && p.id !== product.id && kp.productMasterDataId === p.productMasterDataId);

      return (
        item ||
        new Product({
          companyId: currentCompany.id,
          stockLocationId: product.stockLocationId,
          productMasterDataId: kp.productMasterDataId,
          unitQuantity: '0',
          kitId: kit.id,
        })
      );
    });
  }, [product]);

  const headers: TableHeader<Product>[] = [
    {
      key: 'name',
      name: t(TranslationKey.productName).singular.label,
      text: (item: Product) =>
        productMasterData.get(item.productMasterDataId)?.productName ||
        t(TranslationKey.unknownProductMasterData).singular.label,
      column: ProductTableSettingsColumn.productName,
    },
    {
      key: 'number',
      name: t(TranslationKey.productNumber).singular.label,
      text: (item: Product) =>
        productMasterData.get(item.productMasterDataId)?.productNumber ||
        t(TranslationKey.unknownProductMasterData).singular.label,
      column: ProductTableSettingsColumn.productNumber,
    },
    {
      key: 'quantity',
      name: t(TranslationKey.quantity).singular.label,
      text: (item: Product) => {
        const kit = kits.get(item.kitId || '');
        const kitTemplate = kitTemplates.get(kit?.templateId || '');
        if (!kitTemplate) return '';

        const kitProduct = kitTemplate.products?.find(p => p.productMasterDataId === item.productMasterDataId);
        const pmd = productMasterData.get(item.productMasterDataId);
        return `${UnitOfMeasure.toQuantity(pmd?.unitOfMeasure, item.unitQuantity)}/${
          kitProduct?.quantity
        } ${UnitOfMeasureUtil.getSuffix(pmd?.unitOfMeasure)}`;
        return '';
      },
      column: ProductTableSettingsColumn.quantity,
    },
  ];

  const footer = (
    <div className='flex justify-between gap-2'>
      <div>
        {productId ? (
          <Button
            variant={ButtonVariant.link}
            onClick={() => {
              setOpen(false);
              openCreateOrderFromStockModal(product);
            }}
            text={translate({ key: TranslationKey.stockTransfer })}
          />
        ) : null}
      </div>
      <CancelButton onClick={() => setOpen(false)} />
    </div>
  );

  return (
    <ActionModalPane footer={footer}>
      <div className='grid grid-cols-2 gap-2'>
        <TextInput
          label={t(TranslationKey.stockLocation).singular.label}
          value={stockLocations.get(product?.stockLocationId)?.name || ''}
          disabled={true}
        />
        <TextInput
          label={t(TranslationKey.bin).singular.label}
          value={bins.get(product?.binId)?.name || ''}
          disabled={true}
        />
        {pmd.lotManaged && (
          <TextInput
            label={t(TranslationKey.lot).singular.label}
            value={lots.get(product?.lotId || '')?.number}
            disabled={true}
          />
        )}
        {pmd.serialManaged && (
          <TextInput label={t(TranslationKey.serial).singular.label} value={product.serial || ''} disabled={true} />
        )}
        {pmd.kitManaged && (
          <TextInput
            label={t(TranslationKey.kit).singular.label}
            value={kits.get(product?.kitId || '')?.identifier}
            disabled={true}
          />
        )}
        {product?.containerId && (
          <TextInput
            label={t(TranslationKey.container).singular.label}
            value={containers.get(product?.containerId || '')?.identifier}
            disabled={true}
          />
        )}
      </div>
      {pmd.kitManaged && (
        <div className='mt-2 grid h-64 grid-cols-1'>
          <Table headers={headers} items={kitProducts} />
        </div>
      )}
    </ActionModalPane>
  );
}
